import React, { useState } from 'react';
import { authApi } from '../../../services/authApi';
import { useUserStore } from '../../../store/userStore';
import {
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

interface PasswordChangeModalProps {
  isOpen: boolean;
  onSuccess: () => void;
  onClose?: () => void;
  isFirstLogin?: boolean;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  isOpen,
  onSuccess,
  onClose,
  isFirstLogin = false,
}) => {
  const { currentUser } = useUserStore();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  if (!isOpen) return null;

  const validatePassword = (password: string): { isValid: boolean; error?: string } => {
    if (password.length < 8) {
      return { isValid: false, error: '비밀번호는 최소 8자 이상이어야 합니다.' };
    }

    const hasLetter = /[a-zA-Z]/.test(password);
    const hasDigit = /[0-9]/.test(password);

    if (!hasLetter || !hasDigit) {
      return {
        isValid: false,
        error: '비밀번호는 영문자와 숫자를 포함해야 합니다.'
      };
    }

    return { isValid: true };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 비밀번호 확인
    if (newPassword !== confirmPassword) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    // 비밀번호 정책 검증
    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError(validation.error || '');
      return;
    }

    setLoading(true);
    try {
      // 첫 로그인의 경우 현재 비밀번호 필드를 생략할 수 있도록 빈 문자열 전송
      const currentPwd = isFirstLogin ? '' : currentPassword;
      await authApi.changePassword(currentPwd, newPassword);
      setShowSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (error: any) {
      setError(error.response?.data?.detail || '비밀번호 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {showSuccess ? (
          <div className="text-center">
            <CheckIcon className="mx-auto h-12 w-12 text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">비밀번호 변경 완료</h2>
            <p className="text-gray-600">비밀번호가 성공적으로 변경되었습니다.</p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">비밀번호 변경</h2>
            {isFirstLogin ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-6">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    첫 로그인입니다. 보안을 위해 비밀번호를 변경해주세요.
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-600 mb-6">
                새로운 비밀번호를 입력해주세요.
              </p>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isFirstLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    현재 비밀번호
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  새 비밀번호
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  8자 이상, 영문자와 숫자 포함
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  새 비밀번호 확인
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 px-3 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? '변경 중...' : '비밀번호 변경'}
                </button>
                {!isFirstLogin && onClose && (
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    취소
                  </button>
                )}
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default PasswordChangeModal;